import { IgnoreZone } from "../SiteAboutPageUtils/siteAboutInterfaces";
import { MetricSimplifiedRow } from "../SiteAboutPageUtils/bulkUpdateCamerasApi";

export type PresetZoneLabelPayload = {
  label: number;
  name: string;
  coordinates: number[][][];
};

export type PresetZonePatchBody = {
  name: string;
  labels: PresetZoneLabelPayload[];
};

export type PresetZonePayload = {
  camera: number;
  preset: number;
  name: string;
  labels: PresetZoneLabelPayload[];
};

const normalizeLabelName = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/^ignore[_\s]+/, "")
    .replace(/^ignore\s+/, "")
    .replace(/\s+/g, " ");

const isIgnoreAllZone = (zone: IgnoreZone): boolean => {
  const normalizedLabel = normalizeLabelName(zone.label || "");
  const normalizedDisplay = normalizeLabelName(zone.label_display_name || "");
  return (
    zone.zone_type === 0 ||
    normalizedLabel === "motion" ||
    normalizedLabel === "ignore motion" ||
    normalizedLabel === "ignore all" ||
    normalizedDisplay === "motion" ||
    normalizedDisplay === "ignore motion" ||
    normalizedDisplay === "ignore all"
  );
};

const clampCoordinate = (value: unknown): number | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return value < 0 ? 0 : value;
};

/**
 * Accepts either coordinates as a single polygon (number[][]) or a list of polygons (number[][][]).
 */
const sanitizeCoordinates = (coordinates: unknown): number[][][] => {
  if (!Array.isArray(coordinates)) {
    return [];
  }
  const first = coordinates[0];
  if (!Array.isArray(first)) {
    return [];
  }

  const looksLikePointPair =
    first.length >= 2 &&
    typeof first[0] === "number" &&
    typeof first[1] === "number";

  const polygonGroups: unknown[][] = looksLikePointPair
    ? [coordinates as unknown[]]
    : (coordinates as unknown[][]);

  const cleaned = polygonGroups
    .map((polygon) => {
      if (!Array.isArray(polygon)) {
        return [];
      }
      return polygon
        .map((point) => {
          if (!Array.isArray(point) || point.length < 2) {
            return null;
          }
          const x = clampCoordinate(point[0]);
          const y = clampCoordinate(point[1]);
          if (x === null || y === null) {
            return null;
          }
          return [x, y];
        })
        .filter((point): point is number[] => Array.isArray(point));
    })
    .filter((polygon) => polygon.length > 0);

  return cleaned as number[][][];
};

const candidateNamesForZone = (zone: IgnoreZone): string[] => {
  const candidates = [zone.label, zone.label_display_name]
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .map(normalizeLabelName);
  return Array.from(new Set(candidates));
};

const resolveLabelForZone = (
  cameraId: number,
  zone: IgnoreZone,
  rows: MetricSimplifiedRow[]
): { labelId: number; labelName: string } => {
  const forCamera = rows.filter((row) => Number(row.camera?.id) === cameraId);
  const zoneCandidates = candidateNamesForZone(zone);
  const matchedByName = forCamera.find((row) =>
    zoneCandidates.includes(normalizeLabelName(row.label_name || ""))
  );
  if (matchedByName) {
    return {
      labelId: Number(matchedByName.label_id),
      labelName: matchedByName.label_name,
    };
  }

  if (typeof zone.metric_id === "number") {
    const matchedByMetric = forCamera.find(
      (row) => Number(row.metric_id) === zone.metric_id
    );
    if (matchedByMetric) {
      return {
        labelId: Number(matchedByMetric.label_id),
        labelName: matchedByMetric.label_name,
      };
    }
    // Preset / ignore-zone payloads sometimes store `label_id` in `metric_id`
    // while metric/simplified uses a distinct API `metric_id` per row.
    const matchedByLabelId = forCamera.find(
      (row) => Number(row.label_id) === zone.metric_id
    );
    if (matchedByLabelId) {
      return {
        labelId: Number(matchedByLabelId.label_id),
        labelName: matchedByLabelId.label_name,
      };
    }
  }

  throw new Error(
    `Missing label mapping for camera ${cameraId} zone "${zone.label_display_name || zone.label}".`
  );
};

export const buildPresetZonePayload = ({
  cameraId,
  presetId,
  presetName,
  zones,
  metricRows,
}: {
  cameraId: number;
  presetId: number;
  presetName: string;
  zones: IgnoreZone[];
  metricRows: MetricSimplifiedRow[];
}): PresetZonePayload => {
  const forCamera = metricRows.filter((row) => Number(row.camera?.id) === cameraId);
  const activeLabels = forCamera.filter((row) => row.active !== false);
  const labelsById = new Map<number, PresetZoneLabelPayload>(
    activeLabels.map((row) => [
      Number(row.label_id),
      {
        label: Number(row.label_id),
        name: row.label_name,
        coordinates: [],
      },
    ])
  );

  const ignoreAllPolygons = zones
    .filter((zone) => isIgnoreAllZone(zone))
    .flatMap((zone) => sanitizeCoordinates(zone.coordinates));

  zones
    .filter((zone) => !isIgnoreAllZone(zone))
    .forEach((zone) => {
      const { labelId, labelName } = resolveLabelForZone(cameraId, zone, metricRows);
      const coordinates = sanitizeCoordinates(zone.coordinates);
      const existing = labelsById.get(labelId);
      if (existing) {
        labelsById.set(labelId, {
          ...existing,
          coordinates: [...existing.coordinates, ...coordinates],
        });
        return;
      }
      labelsById.set(labelId, {
        label: labelId,
        name: labelName,
        coordinates,
      });
    });

  if (ignoreAllPolygons.length > 0) {
    labelsById.forEach((entry, labelId) => {
      labelsById.set(labelId, {
        ...entry,
        coordinates: [...ignoreAllPolygons, ...entry.coordinates],
      });
    });
  }

  return {
    camera: cameraId,
    preset: presetId,
    name: presetName,
    labels: Array.from(labelsById.values()),
  };
};

/** PATCH `preset-zone/{row_id}/` body: `name` + `labels` only (per API). */
export const buildPresetZonePatchBodyFromZones = ({
  cameraId,
  presetId,
  presetApiName,
  zones,
  metricRows,
}: {
  cameraId: number;
  presetId: number;
  presetApiName: string;
  zones: IgnoreZone[];
  metricRows: MetricSimplifiedRow[];
}): PresetZonePatchBody => {
  const full = buildPresetZonePayload({
    cameraId,
    presetId,
    presetName: presetApiName,
    zones,
    metricRows,
  });
  return { name: full.name, labels: full.labels };
};
